import { hot } from 'react-hot-loader';
import styled from 'styled-components';
export { default as fetchData } from './fetchData';
import React from 'react';
import MetaData from './components/meta-data';
import Hero from './components/hero';
import MealInfo from './components/meal-info';
import RestaurantInfo from './components/restaurant-info';
import Map from './components/map';
import Delivery from './components/delivery';
import ContactDetails from './components/contact-details';
import OpenHours from './components/open-hours';
import { spacingHelper, paletteHelper } from '../../@nitty'

const ScreenContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${spacingHelper("0 medium")};
    max-width: calc(71.375rem + ${spacingHelper("medium")});


    ${({ theme }) => theme.layoutBreakpoints.medium} {
        padding: ${spacingHelper("0 extraLarge")};
        margin: ${spacingHelper('0 auto')};
        padding-bottom: 5rem;
    }
`

const HeroContainer = styled.div`
    padding: ${spacingHelper("0 medium")};
    margin-top: 1rem;
    max-width: calc(71.375rem + ${spacingHelper("medium")});

    ${({ theme }) => theme.layoutBreakpoints.medium} {
        padding: ${spacingHelper("0 extraLarge")};
        margin: ${spacingHelper('0 auto')};
        margin-top: 2rem;
    }
`

const RowWrapper = styled.div`
    display: flex;
    flex-direction: column;

    ${({ theme }) => theme.layoutBreakpoints.large} {
        flex-direction: row;
        > div {
            flex: 1;
            &:first-child {
                flex: 2;
                margin-right: 2rem;
            }
        }
    }
`

const ErrorContainer = styled.div`
    padding: ${spacingHelper('doubleExtraLarge large')};
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
`

const ErrorHeading = styled.h2`
    margin-bottom: ${spacingHelper('medium')};
    color: ${paletteHelper('textPrimary')};
    font-size: ${({ theme }) => theme?.typography?.h02?.fontSize || '1.5rem'};
    font-weight: ${({ theme }) => theme?.typography?.h02?.fontWeight || '600'};
`

const ErrorMessage = styled.p`
    margin-bottom: ${spacingHelper('large')};
    color: ${paletteHelper('textSecondary')};
    font-size: ${({ theme }) => theme?.typography?.body01?.fontSize || '1.1rem'};
    line-height: ${({ theme }) => theme?.typography?.body01?.lineHeight || '1.5'};
`

const ButtonGroup = styled.div`
    display: flex;
    gap: ${spacingHelper('medium')};
    justify-content: center;
    flex-wrap: wrap;
`

const ErrorButton = styled.button`
    padding: ${spacingHelper('small large')};
    background: ${({ variant }) => variant === 'primary' 
        ? paletteHelper('backgroundBrandStatic') 
        : paletteHelper('backgroundSecondaryBase')};
    color: ${({ variant }) => variant === 'primary' 
        ? paletteHelper('textInverted') 
        : paletteHelper('textPrimary')};
    border: none;
    border-radius: ${({ theme }) => theme?.border?.radius?.medium || '0.25rem'};
    cursor: pointer;
    font-size: ${({ theme }) => theme?.typography?.body02?.fontSize || '1rem'};
    font-weight: ${({ theme }) => theme?.typography?.body02?.fontWeight || '500'};
    transition: opacity 0.2s ease;
    
    &:hover {
        opacity: 0.9;
    }
    
    &:active {
        transform: translateY(1px);
    }
`

const LoadingContainer = styled.div`
    padding: ${spacingHelper('large')};
    text-align: center;
    color: ${paletteHelper('textSecondary')};
    font-size: ${({ theme }) => theme?.typography?.body02?.fontSize || '1rem'};
`

const View = ({ data, location, error }) => {
    // Check for errors first
    if (error || (data && data.error)) {
        const errorInfo = error || data.error;
        return (
            <>
                <MetaData data={{ meal: null }} />
                <ScreenContainer>
                    <ErrorContainer>
                        <ErrorHeading>
                            Oops! Something went wrong
                        </ErrorHeading>
                        <ErrorMessage>
                            {errorInfo.message || 'Unable to load meal details. Please try again later.'}
                        </ErrorMessage>
                        <ButtonGroup>
                            <ErrorButton 
                                variant="primary"
                                onClick={() => window.location.reload()}
                            >
                                Try Again
                            </ErrorButton>
                            <ErrorButton 
                                variant="secondary"
                                onClick={() => window.history.back()}
                            >
                                Go Back
                            </ErrorButton>
                        </ButtonGroup>
                    </ErrorContainer>
                </ScreenContainer>
            </>
        );
    }
    
    // Show loading state if meal data hasn't loaded yet
    if (!data || !data.meal) {
        return (
            <>
                <MetaData data={{ meal: null }} />
                <ScreenContainer>
                    <LoadingContainer>
                        Loading meal details...
                    </LoadingContainer>
                </ScreenContainer>
            </>
        );
    }
    
    return (
        <>
            <MetaData data={data} />
            <div>
                <HeroContainer>
                    <Hero data={data} />
                </HeroContainer>
                <ScreenContainer>
                    <MealInfo data={data} />
                    <RowWrapper>
                        <div>
                            <Map data={data} />
                            <RestaurantInfo data={data} />
                            <Delivery data={data} />
                        </div>
                        <div>
                            <ContactDetails data={data} />
                            <OpenHours data={data} />
                        </div>
                    </RowWrapper>
                </ScreenContainer>
            </div>
        </>
    );
};

export default hot(module)(View);
